import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import { AppService } from '@src/suite/app.service';
import { SuggestionItem, SuggestionType } from '@src/suite/common/components/search-box/simple-search-box/simple-search-box';
import { LookupListItem } from '@src/suite/common/data-models/complex-types/lookup-list';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { ClickStopPropagationDirective } from '../../../directives/stop-propagation';
import { SimpleSearchBoxComponent } from '../simple-search-box/simple-search-box';

@Component({
    selector: 'st-simple-search-box-autocomplete',
    imports: [ClickStopPropagationDirective, SimpleSearchBoxComponent],
    templateUrl: './simple-search-box-autocomplete.html',
    styleUrl: './simple-search-box-autocomplete.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-simple-search-box-autocomplete',
    },
})
export class SimpleSearchBoxAutocompleteComponent implements OnInit, OnDestroy {
    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);

    readonly searchTerm = signal('');
    readonly suggestions = signal<SuggestionItem[]>([]);

    readonly searchExecute = output<string>();

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    ngOnInit() {
        this.searchSubject.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(value => {
            this.searchTerm.set(value);
            this.searchExecute.emit(value);
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchSubject.complete();
    }

    allUsers = Object.values(this.globalTablesService.Staff);

    public async onSearchTermHandler(value: string) {
        value = value?.trim();
        if (value?.startsWith('#')) {
            const response = await this.appService.nomWebServicesJson.getSuggestions(value.replace('#', '').toLowerCase()).then(res => res.data.map(item => new LookupListItem(item, item, item)));

            this.suggestions.set(
                response.map(item => ({
                    value: item.id,
                    label: item.displayText || item.id,
                    type: SuggestionType.TAG,
                }))
            );
        } else if (value?.startsWith('@')) {
            const val = value?.replace('@', '').toLowerCase();
            this.suggestions.set(
                this.allUsers
                    ?.filter(option => option.UserID.toLowerCase().includes(val as string) || `${option.GivenName} ${option.Surname}`.toLowerCase().includes(val as string))
                    .map(item => ({
                        value: item.UserID,
                        label: `${item.GivenName} ${item.Surname}`,
                        type: SuggestionType.STAFF,
                    }))
            );
        } else {
            this.suggestions.set([]);
            this.onSearchHandler(value);
        }
    }

    public onSearchHandler(value: string) {
        this.searchSubject.next(value);
    }
}
